import { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import AddNote from "../modals/addNote";
import AddNonCaseNote from "../modals/addNonCaseTreatment";
import RequestDateOfTreatment from "../modals/requestDateOfTreatment";
import CancelTreatment from "../modals/cancelTreatment";

import { PatientPageCaseListingProps } from "./types";
import { GLOBAL_PATIENT_DETAIL_CASE } from "src/constants/detailPageTable";
import { AuthContext } from "src/context/authContext";
import { handleNavigation } from "src/helpers";
import PracticeContactInfo from "../practiceContactInfo";
import TableHeader from "../tableHeader";
import { sortTableColumns } from "src/helpers/stats";
import { CaseObjectType } from "src/pages/updatePatient/types";
import { ColumnSortProps } from "../tableHeader/types";



const PatientPageCaseListing: FC<PatientPageCaseListingProps> = ({ patientId, caseListing: caseList, patientName, refreshPatientDetails }): JSX.Element => {
  const [caseListing, updateCaseListing] = useState<CaseObjectType[]>([]);
  const [sortingConfig, setSortingConfig] = useState<ColumnSortProps>({
    sort_by: '',
    direction: 'desc',
  });

  const [saveNoteModal, setDataforNoteModal] = useState<{ isModalVisible: boolean; caseId: number; }>({
    isModalVisible: false,
    caseId: 0,
  });
  const [NonCaseNoteModal, setDataforNonCaseNoteModal] = useState<{ isModalVisible: boolean; patientName: string; }>({
    isModalVisible: false,
    patientName: "",
  });
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number, bentype: string; investigated_at: string; }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0,
    investigated_at: "",
    bentype: ''
  });
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId: number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId: 0
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { isManufacturer, defaultView } = useContext(AuthContext)

  useEffect(() => {
    if (caseList.length > 0) {
      updateCaseListing(caseList)
      setSortingConfig({ direction: "desc", sort_by: "" })
    }
  }, [caseList])

  const redirectDecision = () => {
    const isRedirectRequired = handleNavigation(defaultView, location.pathname);
    if (isRedirectRequired) {
      navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true })
    } else {
      navigate(0)
    }
  }


  const changeIdOrder = (value: string) => {
    sortTableColumns(value, sortingConfig, setSortingConfig, caseListing, updateCaseListing)
  };

  return (
    <div className="card my-4 rounded-1">
      <div className="card-header bg-success-subtle">
        <div className="row">
          <div className="col-6 d-flex align-items-center">
            <h4 className="mb-0 text-success fs-4">Cases</h4>
          </div>
          {/* <div className="col-6 text-end">
            <button className="btn btn-sm btn-green my-1"
              onClick={() => {
                patientId && setDataforNonCaseNoteModal({
                  isModalVisible: true,
                  patientName: patientName,
                })
              }}>
              <i className="fas fa-times" aria-hidden="true"></i> Add Tx Date If No BI
            </button>
          </div> */}
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-striped mb-0">
            <thead>
              <tr className="text-center align-items-center">
                {GLOBAL_PATIENT_DETAIL_CASE.map((header, index) => (
                  <TableHeader
                    key={index}
                    classNames="align-middle fw-semibold fs-6"
                    tableHeader={{
                      ...header,
                      width: header.label.toLowerCase().includes("name") ? "40%" : ""
                    }}
                    sortConfig={sortingConfig}
                    changeIdOrder={changeIdOrder}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {caseListing.map((data, index) => {
                const isOffice = data.kam_fam_note?.type.toLowerCase().replace(/note/g, '').trim() === "office"
                const isFam = data.kam_fam_note?.type.toLowerCase().replace(/note/g, '').trim() === "fam"
                return (
                  <tr key={index}>
                    <td className="text-center align-middle fs-6" style={{ minWidth: '85px' }}>
                      {data.indication.toLowerCase() === 'other' ? (
                        <p className="text-danger mb-0">{data.case_id}</p>
                      ) : (
                        <Link
                          to={`/cases/${data.case_id}`}
                          className="btn btn-link link-offset-0 link-offset-0-hover link-underline link-underline-opacity-0 link-underline-opacity-100-hover p-0 align-baseline user-select-text"
                        >
                          {data.case_id}
                        </Link>
                      )}
                    </td>
                    <td className="text-center align-middle fs-6">
                      {data.created}
                    </td>
                    <td className="text-center align-middle fs-6">
                      {data.treated}
                    </td>
                    <td className="text-center align-middle fs-6">
                      {data.date_of_next_treatment}
                    </td>
                    <td className="text-center align-middle fs-6">
                      <PracticeContactInfo
                        name={data.practice_name}
                        address={data.practice_full_address}
                        phone={data.practice_phone_number}
                        fax={data.practice_fax_number}
                        indexKey={index}
                      >
                        <>{data.provider}</>
                      </PracticeContactInfo>
                    </td>
                    <td className={`text-center text-light align-middle fs-6 background-color-${data.status_color}`}>
                      {data.status}
                    </td>
                    <td className="text-center align-middle fs-6" style={{ minWidth: '300px' }}>
                      {isManufacturer && data.indication.toLowerCase() === "other" ?
                        <p className="text-black mb-0">No Action Available</p> :
                        <>
                          {data.next_steps && data.next_steps.length > 0 &&
                            data.next_steps.map((step, index) => {
                              switch (step) {
                                case "Treated 1":
                                  return (
                                    <button key={index} className="btn btn-green btn-sm m-1"
                                      onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: data.case_id, prescriptionId: data.prescription_id, bentype: "med", investigated_at: data.investigated_at || "" })}

                                      type="button">
                                      <i className="far fa-clipboard"></i> Treated
                                    </button>
                                  );
                                case "Treated 2":
                                  return (
                                    <button key={index} className="btn btn-green btn-sm m-1"
                                      onClick={() => setDataForRequestModalTreatment({ isModalVisible: true, caseId: data.case_id, prescriptionId: data.prescription_id, bentype: "na", investigated_at: data.investigated_at || "" })}

                                      type="button">
                                      <i className="far fa-clipboard"></i> Treated
                                    </button>
                                  );
                                case "Not Treating":
                                  return (
                                    <button key={index} className="btn btn-green btn-sm m-1"
                                      onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: data.case_id, prescriptionId: data.prescription_id })}
                                      type="button">
                                      <i className="fas fa-times" aria-hidden="true"></i> Not Treating
                                    </button>
                                  );
                                case "Not Treated":
                                  return (
                                    <button key={index} className="btn btn-green btn-sm m-1"
                                      onClick={() => setDataForCancelModal({ isModalVisible: true, caseId: data.case_id, prescriptionId: data.prescription_id })}
                                      type="button">
                                      <i className="fas fa-times" aria-hidden="true"></i> Not Treating
                                    </button>
                                  );
                                // case "Precert":
                                //   return (
                                //     <button
                                //       key={index}
                                //       className="btn btn-warning btn-sm background-color-5 text-white"
                                //       type="button"
                                //       onClick={() => showToast(["Precert not found!"], ToastTypes.ERROR)}
                                //     >
                                //       <i className="fas fa-clipboard-list"></i> Precert
                                //     </button>
                                //   );
                                // case "Review":
                                //   return (
                                //     <button
                                //       key={index}
                                //       className="btn background-color-2 rounded-0 m-1 text-white"
                                //       type="button"
                                //       onClick={() => navigate(`/cases/${data.case_id}/precerts/${data.review_precert_id || 0}/review`)}
                                //     >
                                //       <i className="fas fa-clipboard-list"></i> Review
                                //     </button>
                                //   );
                                // case "Appeal":
                                //   return (
                                //     <button
                                //       key={index}
                                //       className="btn btn-green background-color-3 rounded-0 m-1 text-white"
                                //       type="button"
                                //       onClick={() => submiAppealForCase(data.case_id, data.appeal_precert_id || 0)}
                                //       disabled={apiInPorgress}
                                //     >
                                //       <i className="fas fa-clipboard-list"></i> Appeal
                                //     </button>
                                //   );
                                case "No Action Required":
                                  return (
                                    <p className="text-black">No Action Available</p>
                                  );
                                default:
                                  return (<></>);
                              }

                            })
                          }
                        </>
                      }
                    </td>

                    <td className="text-center align-middle fs-6">
                      {data.not_treated_reason ?
                        <p className="mb-0">{data.not_treated_reason}</p>
                        : null
                      }
                    </td>
                    <td className={`text-center align-middle fs-6`}>
                      {data.indication}
                    </td>
                    <td className="text-center align-middle fs-6">
                      {data.indication.toLowerCase() !== "other" ?
                        data.kam_fam_note &&
                          data.kam_fam_note.text === 'Add Note' ? (
                          <button type="button" className="btn btn-green btn-sm rounded-0" disabled={!data.kam_fam_note.clickable}
                            onClick={() => { setDataforNoteModal({ isModalVisible: true, caseId: data.case_id }) }} >
                            <i className="fas fa-times"></i>{data.kam_fam_note.text}
                          </button>
                        )
                          :
                          (isManufacturer || !String(data.kam_fam_note?.text).includes("No FAM Action Needed"))
                            ? (
                              <button
                                className={`btn btn-link ${isOffice ? "btn-office" : isFam ? "btn-fam" : ""} user-select-text`}
                                onClick={() =>
                                  setDataforNoteModal({
                                    isModalVisible: true,
                                    caseId: data.case_id
                                  })
                                }
                              >
                                {data.kam_fam_note?.text}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-green btn-sm rounded-0"
                                onClick={() =>
                                  setDataforNoteModal({
                                    isModalVisible: true,
                                    caseId: data.case_id
                                  })
                                }
                              >
                                <i className="fas fa-times me-1"></i> Add Note
                              </button>
                            )
                        : null}
                    </td>
                  </tr>
                )
              }
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddNote
        isModalVisible={saveNoteModal.isModalVisible}
        caseId={saveNoteModal.caseId}
        refreshListing={() => isManufacturer ? refreshPatientDetails() : redirectDecision()}
        hideModal={() => {
          setDataforNoteModal({
            isModalVisible: false,
            caseId: 0,
          });
        }}
      />

      {NonCaseNoteModal.isModalVisible && (
        <AddNonCaseNote
          isModalVisible={NonCaseNoteModal.isModalVisible}
          patientName={NonCaseNoteModal.patientName}
          refreshListing={() => redirectDecision()}
          hideModal={() => {
            setDataforNonCaseNoteModal({
              isModalVisible: false,
              patientName: "",
            });
          }}
        />
      )}

      {showRequestTreatmentModal.isModalVisible && (
        <RequestDateOfTreatment
          isModalVisible={showRequestTreatmentModal.isModalVisible}
          caseId={showRequestTreatmentModal.caseId}
          prescriptionId={showRequestTreatmentModal.prescriptionId}
          bentype={showRequestTreatmentModal.bentype}
          investigated_at={showRequestTreatmentModal.investigated_at}
          hideModal={() => {
            setDataForRequestModalTreatment({
              isModalVisible: false,
              caseId: 0,
              prescriptionId: 0,
              bentype: '',
              investigated_at: "",
            });
          }}
          refreshListing={() => redirectDecision()}
        />
      )}

      <CancelTreatment
        isModalVisible={cancelTreatmentModal.isModalVisible}
        caseId={cancelTreatmentModal.caseId}
        prescriptionId={cancelTreatmentModal.prescriptionId}
        hideModal={() => {
          setDataForCancelModal({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0
          });
        }}
        refreshListing={() => redirectDecision()}
      />
    </div>
  )
}

export default PatientPageCaseListing;
